import { useEffect, useCallback, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { variantColors, type DialogVariant } from './variantColors';
import { Button } from '../Button';

export interface DialogProps {
  isOpen: boolean;
  onClose?: () => void;
  variant?: DialogVariant;
  title?: string;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  allowClickOutsideToClose?: boolean;
}

export const Dialog = memo(function Dialog({
  isOpen,
  onClose,
  variant = 'grey',
  title,
  children,
  className = '',
  showCloseButton = false,
  allowClickOutsideToClose = true
}: DialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  const colors = variantColors[variant];

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose && allowClickOutsideToClose) {
        onClose();
      }
    },
    [onClose, allowClickOutsideToClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement;
    document.addEventListener('keydown', handleKeyDown);
    dialogRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, handleKeyDown]);

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          <motion.div
            className="absolute inset-0 z-40 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            onClick={(e) => {
              if (e.target === e.currentTarget && onClose && allowClickOutsideToClose) {
                onClose();
              }
            }}
            aria-hidden="true"
          />

          <div
            className="absolute inset-0 z-50 flex items-center justify-center px-4 py-8"
            style={{ pointerEvents: 'none' }}
          >
            <motion.div
              ref={dialogRef}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1]
              }}
              className={`relative w-full max-w-[340px] rounded-3xl border-4 ${className}`}
              style={{
                background: `linear-gradient(180deg, ${colors.background.start} 0%, ${colors.background.end} 100%)`,
                borderColor: colors.border,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                overflow: 'hidden',
                pointerEvents: 'auto',
                maxHeight: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? 'dialog-title' : undefined}
              tabIndex={-1}
            >
              {showCloseButton && onClose && (
                <Button
                  onClick={onClose}
                  variant="red"
                  aria-label={t('accessibility.closeDialog')}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    width: '32px',
                    height: '32px',
                    minWidth: '32px',
                    minHeight: '32px',
                    maxWidth: '32px',
                    maxHeight: '32px',
                    padding: 0,
                    zIndex: 20
                  }}
                >
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>
              )}

              {title && (
                <div
                  className="py-2 px-4"
                  style={{
                    backgroundColor: colors.border,
                    flexShrink: 0
                  }}
                >
                  <h2
                    id="dialog-title"
                    className="font-mind-boggle text-2xl text-white text-center leading-tight"
                    style={{
                      textShadow:
                        '3px 3px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 2px 2px 6px rgba(0,0,0,0.5)'
                    }}
                  >
                    {title}
                  </h2>
                </div>
              )}

              <div
                data-testid="dialog-content"
                className={`bg-white p-4 ${title ? '' : 'rounded-t-3xl'} rounded-b-3xl`}
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  // Stops the scroll chaining to the page behind once the
                  // content hits its end.
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {children}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
});

export default Dialog;
